/**
 * useReceiptImage.ts — Captura un nodo DOM como PNG y lo comparte/descarga.
 *
 * QUÉ HACE:
 *  - Recibe un ref a un elemento (típicamente el ticket renderizado).
 *  - Lo captura con html2canvas y produce un PNG blob.
 *  - Expone tres acciones equivalentes a useReceiptPdf pero para imagen:
 *      · downloadImage(ref)         → descarga local el PNG
 *      · generateImageBlob(ref)     → devuelve { blob, filename } para uso externo
 *      · shareImage(ref, t?, b?)    → intenta navigator.share con el PNG; cae a download
 *
 * QUÉ EXPORTA:
 *  - useReceiptImage({ filename, scale }) → API descrita arriba + isGenerating + canShareFiles.
 *
 * QUÉ NO HACE:
 *  - No conoce de tickets, ventas, cierres ni de backend.
 *  - No depende de la UI (sólo del ref).
 *
 * REGLAS:
 *  - Usar PNG (no JPEG) para texto nítido en tickets.
 *  - html2canvas se importa dinámico para no inflar el bundle inicial.
 *  - Compartir como `image/png` (WhatsApp/iOS/Android lo aceptan como imagen real).
 */

import { logger } from "@/lib/logger";
import { useState, useCallback, useMemo, RefObject } from "react";
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { captureReceiptCanvas } from "@/lib/utils/receiptCapture";

interface UseReceiptImageOptions {
  /** Nombre del archivo sin extensión */
  filename?: string;
  /** Escala de captura (mayor = más nítido). Default: 2 */
  scale?: number;
  /** Color de fondo. Default: #ffffff */
  backgroundColor?: string;
}

interface ImageBlobResult {
  blob: Blob;
  filename: string;
}

interface UseReceiptImageReturn {
  /** Genera y descarga el PNG. Devuelve true si la descarga arrancó. */
  downloadImage: (ref: RefObject<HTMLElement>) => Promise<boolean>;
  generateImageBlob: (
    ref: RefObject<HTMLElement>,
  ) => Promise<ImageBlobResult | null>;
  /**
   * Intenta compartir vía Web Share API. Devuelve true si el sistema confirmó
   * la acción (no cancelada por el usuario). Si el OS no soporta share de
   * archivos, cae a `downloadImage` y devuelve true si la descarga arrancó.
   */
  shareImage: (
    ref: RefObject<HTMLElement>,
    shareTitle?: string,
    shareText?: string,
  ) => Promise<boolean>;
  isGenerating: boolean;
  canShareFiles: boolean;
}

const detectCanShareFiles = (): boolean => {
  // En Capacitor native usamos plugins (@capacitor/share + filesystem),
  // que sí soportan archivos en iOS/Android.
  if (Capacitor.isNativePlatform()) return true;
  if (typeof navigator === "undefined") return false;
  if (!("share" in navigator) || !("canShare" in navigator)) return false;
  try {
    const probe = new File([new Blob(["x"])], "probe.png", {
      type: "image/png",
    });
    return navigator.canShare({ files: [probe] });
  } catch {
    return false;
  }
};

/** Convierte un Blob a string base64 (sin prefijo data:) — robusto en APK. */
async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const r = reader.result;
      if (typeof r !== "string") return reject(new Error("read fail"));
      const idx = r.indexOf(",");
      resolve(idx >= 0 ? r.slice(idx + 1) : r);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/**
 * Persiste un blob en cache nativo y devuelve un URI listo para `Share.share`.
 * Devuelve null si no estamos en native o falla la escritura.
 */
async function writeBlobToNativeCache(
  blob: Blob,
  filename: string,
): Promise<string | null> {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    const data = await blobToBase64(blob);
    const res = await Filesystem.writeFile({
      path: filename,
      data,
      directory: Directory.Cache,
    });
    return res.uri;
  } catch (err) {
    logger.error("[writeBlobToNativeCache] error:", err);
    return null;
  }
}

export function useReceiptImage({
  filename = "ticket",
  scale,
  backgroundColor = "#ffffff",
}: UseReceiptImageOptions = {}): UseReceiptImageReturn {
  const [isGenerating, setIsGenerating] = useState(false);
  const canShareFiles = useMemo(detectCanShareFiles, []);

  const generateImageBlob = useCallback(
    async (ref: RefObject<HTMLElement>): Promise<ImageBlobResult | null> => {
      if (!ref.current || isGenerating) return null;
      setIsGenerating(true);
      try {
        const { canvas } = await captureReceiptCanvas(ref.current, {
          backgroundColor,
          ...(scale ? { scale } : {}),
        });
        const blob = await new Promise<Blob | null>((resolve) =>
          canvas.toBlob((b) => resolve(b), "image/png", 1),
        );
        if (!blob) return null;
        return { blob, filename: `${filename}.png` };
      } catch (err) {
        logger.error("[useReceiptImage.generateImageBlob] error:", err);
        return null;
      } finally {
        setIsGenerating(false);
      }
    },
    [filename, scale, backgroundColor, isGenerating],
  );

  const triggerDownload = (blob: Blob, name: string): void => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  /**
   * En APK guardamos en Documents para que quede accesible al usuario.
   * Si falla, caemos a Cache para al menos no perder el archivo.
   */
  const nativeSaveAsDownload = async (
    blob: Blob,
    name: string,
  ): Promise<boolean> => {
    try {
      const data = await blobToBase64(blob);
      try {
        await Filesystem.writeFile({
          path: name,
          data,
          directory: Directory.Documents,
        });
      } catch {
        await Filesystem.writeFile({
          path: name,
          data,
          directory: Directory.Cache,
        });
      }
      return true;
    } catch (err) {
      logger.error("[nativeSaveAsDownload] error:", err);
      return false;
    }
  };

  const downloadImage = useCallback(
    async (ref: RefObject<HTMLElement>): Promise<boolean> => {
      const result = await generateImageBlob(ref);
      if (!result) return false;
      if (Capacitor.isNativePlatform()) {
        return nativeSaveAsDownload(result.blob, result.filename);
      }
      triggerDownload(result.blob, result.filename);
      return true;
    },
    [generateImageBlob],
  );

  const shareImage = useCallback(
    async (
      ref: RefObject<HTMLElement>,
      shareTitle?: string,
      shareText?: string,
    ): Promise<boolean> => {
      const result = await generateImageBlob(ref);
      if (!result) return false;

      // ─── Native (Capacitor) ──────────────────────────────────────────
      // Aquí navigator.share / canShare suelen NO funcionar con files en
      // el WebView Android, así que usamos los plugins oficiales.
      if (Capacitor.isNativePlatform()) {
        const uri = await writeBlobToNativeCache(result.blob, result.filename);
        if (!uri) {
          // Fallback: guardarlo y avisar
          return nativeSaveAsDownload(result.blob, result.filename);
        }
        try {
          await Share.share({
            title: shareTitle,
            text: shareText,
            url: uri,
            dialogTitle: shareTitle,
          });
          return true;
        } catch (err) {
          logger.warn("[useReceiptImage.shareImage native] canceled:", err);
          return false;
        }
      }

      // ─── Web Share API (PWA / desktop con soporte) ────────────────────
      const file = new File([result.blob], result.filename, {
        type: "image/png",
      });
      try {
        if (
          typeof navigator !== "undefined" &&
          "canShare" in navigator &&
          navigator.canShare({ files: [file] }) &&
          "share" in navigator
        ) {
          await navigator.share({
            files: [file],
            title: shareTitle,
            text: shareText,
          });
          return true;
        }
      } catch (err) {
        // El usuario canceló el share nativo: NO se considera entregado.
        logger.warn("[useReceiptImage.shareImage] share canceled:", err);
        return false;
      }
      // OS no soporta share de archivos → caemos a descarga (sí cuenta).
      triggerDownload(result.blob, result.filename);
      return true;
    },
    [generateImageBlob],
  );

  return {
    downloadImage,
    generateImageBlob,
    shareImage,
    isGenerating,
    canShareFiles,
  };
}
